import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { apiRequest } from '../../services/apiClient';
import { useToast } from '../../context/ToastContext';

const STATUS_REQUESTS = {
  ACTIVE: '/admin/products?status=ACTIVE&page=1&pageSize=1',
  INACTIVE: '/admin/products?status=INACTIVE&page=1&pageSize=1',
  SOLD: '/admin/products?status=SOLD&page=1&pageSize=1',
};

const extractTotal = (payload) => {
  if (Array.isArray(payload)) {
    return payload.length;
  }

  return Number(payload?.total || 0);
};

const StatCard = ({ icon, title, value, subtitle, tone = 'primary' }) => {
  const toneMap = {
    primary: 'bg-primary-100 text-primary border-primary-500',
    success: 'bg-success-100 text-success border-success-500',
    warning: 'bg-warning-100 text-warning border-warning-500',
    accent: 'bg-accent-100 text-accent-600 border-accent-500',
  };

  return (
    <article className="bg-surface border border-border rounded-xl p-5 shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-text-secondary">{title}</p>
          <p className="mt-1 text-3xl font-bold text-text-primary">{value}</p>
          {subtitle ? <p className="mt-2 text-xs text-text-secondary">{subtitle}</p> : null}
        </div>
        <div className={`p-3 rounded-md border ${toneMap[tone] || toneMap.primary}`}>
          <Icon name={icon} size={20} />
        </div>
      </div>
    </article>
  );
};

const AdminDashboard = () => {
  const { error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    inactiveProducts: 0,
    soldProducts: 0,
    groups: 0,
    productNames: 0,
    productTypes: 0,
    countries: 0,
  });
  const [latestProducts, setLatestProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);

      try {
        const [
          totalProducts,
          activeProducts,
          inactiveProducts,
          soldProducts,
          productGroups,
          productNames,
          productTypes,
          countries,
          publicProducts,
        ] = await Promise.all([
          apiRequest('/admin/products?page=1&pageSize=1', { auth: true }),
          apiRequest(STATUS_REQUESTS.ACTIVE, { auth: true }),
          apiRequest(STATUS_REQUESTS.INACTIVE, { auth: true }),
          apiRequest(STATUS_REQUESTS.SOLD, { auth: true }),
          apiRequest('/admin/master-data/product-groups?includeInactive=true', { auth: true }),
          apiRequest('/admin/master-data/product-names?includeInactive=true', { auth: true }),
          apiRequest('/admin/master-data/product-types?includeInactive=true', { auth: true }),
          apiRequest('/admin/master-data/countries?includeInactive=true', { auth: true }),
          apiRequest('/products?page=1&pageSize=6'),
        ]);

        if (cancelled) {
          return;
        }

        setStats({
          totalProducts: extractTotal(totalProducts),
          activeProducts: extractTotal(activeProducts),
          inactiveProducts: extractTotal(inactiveProducts),
          soldProducts: extractTotal(soldProducts),
          groups: Array.isArray(productGroups) ? productGroups.length : 0,
          productNames: Array.isArray(productNames) ? productNames.length : 0,
          productTypes: Array.isArray(productTypes) ? productTypes.length : 0,
          countries: Array.isArray(countries) ? countries.length : 0,
        });

        const items = Array.isArray(publicProducts) ? publicProducts : publicProducts?.items || [];
        setLatestProducts(items.slice(0, 6));
      } catch (error) {
        if (!cancelled) {
          toastError(error?.message || 'Dashboard verileri alınamadı.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [toastError]);

  const marketSummary = useMemo(() => {
    if (!stats.totalProducts) {
      return 'Henüz ürün kaydı bulunmuyor.';
    }

    const activeRate = ((stats.activeProducts / stats.totalProducts) * 100).toFixed(1);
    return `${stats.totalProducts} toplam ürünün %${activeRate} kadarı aktif yayında.`;
  }, [stats.activeProducts, stats.totalProducts]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          <section className="rounded-2xl border border-border bg-surface p-6 lg:p-8 shadow-elevation-1">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-wide text-primary font-semibold">Superadmin Panel</p>
                <h1 className="mt-1 text-3xl lg:text-4xl font-bold text-text-primary font-heading">Genel Dashboard</h1>
                <p className="mt-2 text-text-secondary">Platform geneli ürün, katalog ve yayın durumunu tek ekranda takip edin.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/general-management-settings"
                  className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors duration-200"
                >
                  <Icon name="Settings" size={16} />
                  Yönetim Ayarları
                </Link>
                <Link
                  to="/property-listings"
                  className="inline-flex items-center gap-2 bg-secondary-100 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-secondary-200 transition-colors duration-200"
                >
                  <Icon name="Search" size={16} />
                  Public Ürünler
                </Link>
              </div>
            </div>
          </section>

          {loading ? (
            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="h-32 rounded-xl bg-secondary-100 animate-pulse" />
              ))}
            </section>
          ) : (
            <>
              <section>
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-text-primary">Platform Ürün Özeti</h2>
                  <p className="text-sm text-text-secondary">{marketSummary}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <StatCard icon="Boxes" title="Toplam Ürün" value={stats.totalProducts} tone="primary" />
                  <StatCard icon="CircleCheck" title="Aktif Ürün" value={stats.activeProducts} tone="success" />
                  <StatCard icon="CircleSlash" title="Pasif Ürün" value={stats.inactiveProducts} tone="warning" />
                  <StatCard icon="BadgeCheck" title="Satılan Ürün" value={stats.soldProducts} tone="accent" />
                </div>
              </section>

              <section>
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-text-primary">Master Data Özeti</h2>
                  <p className="text-sm text-text-secondary">Satıcıların kullandığı tüm sözlük verileri</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <StatCard icon="Layers" title="Ürün Grubu" value={stats.groups} tone="primary" />
                  <StatCard icon="Tag" title="Ürün Adı" value={stats.productNames} tone="success" />
                  <StatCard icon="List" title="Ürün Türü" value={stats.productTypes} tone="accent" />
                  <StatCard icon="Globe" title="Ülke" value={stats.countries} tone="warning" />
                </div>
              </section>

              <section className="bg-surface border border-border rounded-xl shadow-elevation-1 overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-text-primary">Son Eklenen Public Ürünler</h2>
                  <Link to="/property-listings" className="text-sm text-primary hover:underline">
                    Tümünü Gör
                  </Link>
                </div>

                <div className="divide-y divide-border">
                  {latestProducts.length === 0 ? (
                    <div className="px-5 py-10 text-center text-text-secondary">
                      Public listede ürün bulunmuyor.
                    </div>
                  ) : (
                    latestProducts.map((product) => (
                      <div key={product.id} className="px-5 py-4 flex items-center justify-between gap-4">
                        <div>
                          <p className="font-medium text-text-primary">{product.title}</p>
                          <p className="text-sm text-text-secondary">
                            {product.location || 'Lokasyon belirtilmemiş'} • {product.category || 'genel'}
                          </p>
                        </div>
                        <Link
                          to={`/property-details?id=${product.id}`}
                          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          Detay
                          <Icon name="ArrowRight" size={14} />
                        </Link>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
